import React, { useState } from "react";
import axios from "axios";
import { useLang } from "@/contexts/LanguageContext";
import { SITE } from "@/lib/i18n";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Instagram, Facebook, Music2, MessageCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AppointmentForm = () => {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    preferred_date: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error(t.form.error);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      setDone(true);
      toast.success(t.form.success);
      setForm({
        name: "",
        email: "",
        phone: "",
        service: "",
        preferred_date: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error(t.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="appointment-section"
      className="relative bg-[#F9F8F6] py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <p className="overline mb-6 reveal">— {t.form.overline}</p>
          <h2 className="reveal font-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1A1A1A]">
            {t.form.title}{" "}
            <span className="italic text-[#D4AF37]">
              {t.form.title_accent}
            </span>
          </h2>
          <p className="reveal mt-6 text-[#666666] text-base md:text-lg leading-relaxed max-w-md">
            {t.form.sub}
          </p>

          <div className="mt-10 space-y-5 text-sm text-[#1A1A1A]">
            <div className="flex items-start gap-4">
              <Phone size={16} className="text-[#D4AF37] mt-1" />
              <a href={`tel:${SITE.phone_display.replace(/\s/g, "")}`} className="hover:text-[#D4AF37] transition-colors">
                {SITE.phone_display}
              </a>
            </div>
            <div className="flex items-start gap-4">
              <MessageCircle size={16} className="text-[#25D366] mt-1" />
              <a
                href={`https://wa.me/${SITE.whatsapp_number}?text=${encodeURIComponent(SITE.whatsapp_text_es)}`}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-link"
                className="hover:text-[#25D366] transition-colors"
              >
                {SITE.whatsapp_display} <span className="text-xs text-[#666] tracking-widest uppercase ml-1">WhatsApp</span>
              </a>
            </div>
            <div className="flex items-start gap-4">
              <Mail size={16} className="text-[#D4AF37] mt-1" />
              <a href={`mailto:${SITE.email}`} className="hover:text-[#D4AF37] transition-colors">
                {SITE.email}
              </a>
            </div>
            <div className="flex items-start gap-4">
              <MapPin size={16} className="text-[#D4AF37] mt-1" />
              <span>{SITE.address}</span>
            </div>
            <div className="flex items-start gap-4">
              <Clock size={16} className="text-[#D4AF37] mt-1" />
              <span>{t.footer.hours_v}</span>
            </div>
          </div>

          <div className="mt-10 flex items-center gap-3">
            <a
              href={SITE.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="social-instagram"
              className="w-10 h-10 rounded-full border border-[#1A1A1A]/15 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              <Instagram size={16} />
            </a>
            <a
              href={SITE.facebook}
              target="_blank"
              rel="noreferrer"
              data-testid="social-facebook"
              className="w-10 h-10 rounded-full border border-[#1A1A1A]/15 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              <Facebook size={16} />
            </a>
            <a
              href={SITE.tiktok}
              target="_blank"
              rel="noreferrer"
              data-testid="social-tiktok"
              aria-label="TikTok"
              className="w-10 h-10 rounded-full border border-[#1A1A1A]/15 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              <Music2 size={16} />
            </a>
          </div>

          {/* Map */}
          <div className="mt-10 rounded-3xl overflow-hidden border border-[#E5E5E5] aspect-video bg-white">
            <iframe
              title="Multident map"
              data-testid="clinic-map"
              src={SITE.map_embed}
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <form
          data-testid="appointment-form"
          onSubmit={submit}
          className="lg:col-span-7 bg-white border border-[#E5E5E5] rounded-3xl p-8 md:p-12 reveal"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label={t.form.name} required>
              <input
                data-testid="form-name"
                value={form.name}
                onChange={update("name")}
                required
                className="md-input"
                placeholder="Sofía López"
              />
            </Field>
            <Field label={t.form.email} required>
              <input
                data-testid="form-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                className="md-input"
                placeholder="tu@correo.com"
              />
            </Field>
            <Field label={t.form.phone} required>
              <input
                data-testid="form-phone"
                value={form.phone}
                onChange={update("phone")}
                required
                className="md-input"
                placeholder="+52 55 1234 5678"
              />
            </Field>
            <Field label={t.form.service} required>
              <select
                data-testid="form-service"
                value={form.service}
                onChange={update("service")}
                required
                className="md-input bg-white"
              >
                <option value="">{t.form.select_service}</option>
                {t.services.list.map((s, i) => (
                  <option key={i} value={s.t}>
                    {s.t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t.form.date}>
              <input
                data-testid="form-date"
                type="date"
                value={form.preferred_date}
                onChange={update("preferred_date")}
                className="md-input"
              />
            </Field>
            <div />
            <div className="sm:col-span-2">
              <Field label={t.form.message}>
                <textarea
                  data-testid="form-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  className="md-input resize-none"
                  placeholder="…"
                />
              </Field>
            </div>
          </div>

          <button
            data-testid="form-submit"
            type="submit"
            disabled={loading}
            className="btn-premium mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-white text-sm font-medium px-8 py-4 rounded-full disabled:opacity-60"
          >
            {loading ? t.form.sending : t.form.submit}
          </button>

          {done && (
            <p
              data-testid="form-success"
              className="mt-6 text-sm text-[#1A1A1A] bg-[#F9F8F6] border border-[#D4AF37]/40 rounded-xl px-4 py-3"
            >
              {t.form.success}
            </p>
          )}
        </form>
      </div>

      <style>{`
        .md-input {
          width: 100%;
          padding: 14px 16px;
          background: #F9F8F6;
          border: 1px solid #E5E5E5;
          border-radius: 14px;
          color: #1A1A1A;
          font-family: 'Manrope', sans-serif;
          font-size: 14px;
          outline: none;
          transition: border-color .25s ease, background .25s ease;
        }
        .md-input:focus {
          border-color: #D4AF37;
          background: #FFFFFF;
        }
      `}</style>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.18em] text-[#666666] mb-2">
      {label} {required && <span className="text-[#D4AF37]">*</span>}
    </span>
    {children}
  </label>
);
